import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Frozen analytical extract supplied with the colleague's 2020–2024 report.
// It is intentionally separate from the operational map database for now.
const ANNUAL_ROWS = [
  { year: 2020, accidents: 7992, deaths: 377, injured: 8995, fatalAccidents: 343, childAccidents: 648, fatalShare: 4.2918, childShare: 8.1081 },
  { year: 2021, accidents: 8586, deaths: 359, injured: 9720, fatalAccidents: 333, childAccidents: 857, fatalShare: 3.8784, childShare: 9.9814 },
  { year: 2022, accidents: 7629, deaths: 300, injured: 8700, fatalAccidents: 278, childAccidents: 828, fatalShare: 3.644, childShare: 10.8533 },
  { year: 2023, accidents: 7977, deaths: 310, injured: 8826, fatalAccidents: 292, childAccidents: 928, fatalShare: 3.6605, childShare: 11.6334 },
  { year: 2024, accidents: 8947, deaths: 348, injured: 9902, fatalAccidents: 327, childAccidents: 1195, fatalShare: 3.6549, childShare: 13.3564 },
];

// [year, month, accidents, deaths, injured, child accidents]
const MONTHLY_ROWS = [
  [2020, 1, 790, 24, 934, 66], [2020, 2, 743, 32, 828, 55], [2020, 3, 665, 25, 772, 49], [2020, 4, 232, 26, 252, 2], [2020, 5, 323, 25, 368, 14], [2020, 6, 597, 26, 650, 46], [2020, 7, 696, 35, 813, 73], [2020, 8, 793, 28, 894, 84], [2020, 9, 851, 40, 946, 96], [2020, 10, 818, 39, 893, 56], [2020, 11, 739, 34, 834, 63], [2020, 12, 745, 43, 811, 44],
  [2021, 1, 482, 20, 559, 38], [2021, 2, 549, 23, 608, 48], [2021, 3, 684, 20, 790, 67], [2021, 4, 688, 19, 762, 87], [2021, 5, 738, 29, 837, 87], [2021, 6, 710, 30, 807, 73], [2021, 7, 768, 26, 884, 75], [2021, 8, 801, 37, 934, 73], [2021, 9, 830, 40, 913, 90], [2021, 10, 812, 43, 932, 85], [2021, 11, 785, 41, 870, 84], [2021, 12, 739, 31, 824, 50],
  [2022, 1, 538, 18, 653, 46], [2022, 2, 502, 24, 569, 51], [2022, 3, 611, 22, 703, 68], [2022, 4, 630, 29, 698, 68], [2022, 5, 695, 24, 776, 96], [2022, 6, 745, 33, 837, 76], [2022, 7, 676, 25, 756, 59], [2022, 8, 671, 23, 780, 86], [2022, 9, 673, 22, 770, 89], [2022, 10, 698, 24, 806, 85], [2022, 11, 614, 35, 680, 59], [2022, 12, 576, 21, 672, 45],
  [2023, 1, 530, 19, 605, 48], [2023, 2, 531, 20, 607, 51], [2023, 3, 555, 15, 634, 52], [2023, 4, 728, 34, 779, 110], [2023, 5, 744, 23, 800, 114], [2023, 6, 670, 26, 728, 80], [2023, 7, 695, 33, 779, 58], [2023, 8, 842, 28, 933, 109], [2023, 9, 759, 18, 856, 107], [2023, 10, 791, 41, 856, 86], [2023, 11, 653, 30, 725, 65], [2023, 12, 479, 23, 524, 48],
  [2024, 1, 461, 15, 511, 41], [2024, 2, 489, 19, 533, 41], [2024, 3, 532, 28, 610, 68], [2024, 4, 811, 23, 902, 157], [2024, 5, 825, 37, 905, 140], [2024, 6, 880, 27, 963, 125], [2024, 7, 905, 40, 995, 101], [2024, 8, 790, 30, 865, 94], [2024, 9, 971, 34, 1079, 163], [2024, 10, 896, 37, 999, 112], [2024, 11, 709, 31, 784, 86], [2024, 12, 678, 27, 756, 67],
].map(([year, month, accidents, deaths, injured, childAccidents]) => ({ year, month, accidents, deaths, injured, childAccidents }));

const MONTH_LABELS = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
const formatNumber = new Intl.NumberFormat("ru-RU");
const PLASMA_STOPS = ["#0d0887", "#46039f", "#7201a8", "#9c179e", "#bd3786", "#d8576b", "#ed7953", "#fb9f3a", "#fdca26", "#f0f921"];

function plasmaColor(value) {
  const normalized = Math.max(0, Math.min(1, (value - 200) / 800));
  const index = Math.min(PLASMA_STOPS.length - 2, Math.floor(normalized * (PLASMA_STOPS.length - 1)));
  const position = normalized * (PLASMA_STOPS.length - 1) - index;
  const from = PLASMA_STOPS[index].match(/\w\w/g).map((part) => Number.parseInt(part, 16));
  const to = PLASMA_STOPS[index + 1].match(/\w\w/g).map((part) => Number.parseInt(part, 16));
  return `rgb(${from.map((component, channel) => Math.round(component + (to[channel] - component) * position)).join(", ")})`;
}

function formatDelta(value, previous) {
  if (!previous) return "Весь период";
  const percent = ((value / previous) - 1) * 100;
  const direction = percent > 0 ? "▲" : percent < 0 ? "▼" : "•";
  return `${direction} ${Math.abs(percent).toFixed(1)}% к предыдущему году`;
}

function ReportCard({ title, children, className = "" }) {
  return (
    <section className={`statistics-report-card ${className}`.trim()}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((item) => (
        <div key={item.dataKey}>
          <span style={{ background: item.color }} />
          {item.name}: {formatNumber.format(item.value ?? 0)}
        </div>
      ))}
    </div>
  );
}

export default function OverviewReportSection() {
  const [selectedYear, setSelectedYear] = useState("2024");
  const selectedRow = useMemo(() => {
    if (selectedYear !== "all") return ANNUAL_ROWS.find((row) => String(row.year) === selectedYear) || ANNUAL_ROWS.at(-1);
    const totalAccidents = ANNUAL_ROWS.reduce((sum, row) => sum + row.accidents, 0);
    return {
      year: "2020–2024",
      accidents: totalAccidents,
      deaths: ANNUAL_ROWS.reduce((sum, row) => sum + row.deaths, 0),
      injured: ANNUAL_ROWS.reduce((sum, row) => sum + row.injured, 0),
      childAccidents: ANNUAL_ROWS.reduce((sum, row) => sum + row.childAccidents, 0),
      fatalShare: (ANNUAL_ROWS.reduce((sum, row) => sum + row.fatalAccidents, 0) / totalAccidents) * 100,
    };
  }, [selectedYear]);
  const previousRow = selectedYear === "all" ? null : ANNUAL_ROWS.find((row) => row.year === Number(selectedYear) - 1);
  const selectedMonthlyRows = useMemo(
    () => MONTHLY_ROWS.filter((row) => selectedYear === "all" || String(row.year) === selectedYear)
      .map((row) => ({ ...row, label: selectedYear === "all" ? `${row.year}-${String(row.month).padStart(2, "0")}` : MONTH_LABELS[row.month - 1] })),
    [selectedYear]
  );
  const seasonalityRows = useMemo(
    () => [...ANNUAL_ROWS].reverse().map((annual) => ({
      year: annual.year,
      values: MONTHLY_ROWS.filter((row) => row.year === annual.year).sort((left, right) => left.month - right.month),
    })),
    []
  );
  const recentYearsRows = useMemo(
    () => MONTHLY_ROWS.filter((row) => row.year === 2023 || row.year === 2024)
      .reduce((result, row) => {
        const current = result.find((item) => item.month === row.month) || { month: row.month };
        current[String(row.year)] = row.accidents;
        if (!result.includes(current)) result.push(current);
        return result;
      }, []),
    []
  );
  const kpis = [
    { label: "ДТП", value: selectedRow.accidents, previous: previousRow?.accidents },
    { label: "Погибшие", value: selectedRow.deaths, previous: previousRow?.deaths },
    { label: "Пострадавшие", value: selectedRow.injured, previous: previousRow?.injured },
    { label: "ДТП с детьми", value: selectedRow.childAccidents, previous: previousRow?.childAccidents },
  ];

  return (
    <div className="statistics-overview-report">
      <div className="statistics-report-filterbar">
        <label>
          <span>Год для KPI и месячной динамики</span>
          <select value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)}>
            {[...ANNUAL_ROWS].reverse().map((row) => <option key={row.year} value={row.year}>{row.year}</option>)}
            <option value="all">2020–2024</option>
          </select>
        </label>
        <p>Данные — готовый аналитический срез коллеги за 2020–2024; фильтр не пересчитывает показатели из текущей БД.</p>
      </div>

      <div className="statistics-report-kpi-grid">
        {kpis.map((kpi) => (
          <article className="statistics-report-kpi" key={kpi.label}>
            <span>{kpi.label}</span>
            <strong>{formatNumber.format(kpi.value)}</strong>
            <small className={kpi.previous && kpi.value < kpi.previous ? "is-down" : ""}>{formatDelta(kpi.value, kpi.previous)}</small>
          </article>
        ))}
        <article className="statistics-report-kpi">
          <span>Доля смертельных ДТП</span>
          <strong>{Number(selectedRow.fatalShare).toFixed(2)}%</strong>
          <small>Доля ДТП, а не число погибших</small>
        </article>
      </div>

      <div className="statistics-report-section-heading">
        <h3>Месячная динамика выбранного года</h3>
        <p>Количество ДТП, погибших и пострадавших.</p>
      </div>
      <ReportCard className="statistics-report-chart-card">
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={selectedMonthlyRows} margin={{ top: 24, right: 56, bottom: 12, left: 6 }}>
            <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="accidents" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="deaths" orientation="right" tick={{ fontSize: 12 }} />
            <Tooltip content={<ChartTooltip />} />
            <Legend verticalAlign="top" height={34} />
            <Line yAxisId="accidents" type="linear" dataKey="accidents" name="ДТП" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="deaths" type="linear" dataKey="deaths" name="Погибшие" stroke="#b91c1c" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="accidents" type="linear" dataKey="injured" name="Пострадавшие" stroke="#ea580c" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ReportCard>

      <div className="statistics-report-section-heading"><h3>Годовой масштаб</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Годовая динамика количества ДТП" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={ANNUAL_ROWS} margin={{ top: 18, right: 18, bottom: 8, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis domain={[7500, 9100]} ticks={[7600, 7800, 8000, 8200, 8400, 8600, 8800, 9000]} tick={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="linear" dataKey="accidents" name="ДТП" stroke="#0f766e" strokeWidth={3} dot={{ r: 4 }}>
                <LabelList dataKey="accidents" position="top" offset={10} fill="#0f766e" fontSize={11} formatter={(value) => formatNumber.format(value)} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Погибшие и пострадавшие по годам" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={ANNUAL_ROWS} margin={{ top: 18, right: 46, bottom: 8, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis yAxisId="deaths" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="injured" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend verticalAlign="top" height={30} />
              <Bar yAxisId="deaths" dataKey="deaths" name="Погибшие" fill="#b91c1c" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="injured" dataKey="injured" name="Пострадавшие" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Сезонность и сравнение последних лет</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Тепловая карта месячной аварийности" className="statistics-report-chart-card">
          <div className="statistics-plasma-heatmap" role="table" aria-label="Месячная аварийность по годам">
            <span className="statistics-plasma-heatmap-y-title">Год</span>
            <div className="statistics-plasma-heatmap-grid">
              {seasonalityRows.map((row) => (
                <div className="statistics-plasma-heatmap-row" role="row" key={row.year}>
                  <strong>{row.year}</strong>
                  {row.values.map((value) => (
                    <span
                      key={value.month}
                      style={{ background: plasmaColor(value.accidents), color: value.accidents < 600 ? "#fff" : "#172033" }}
                    >
                      {value.accidents}
                    </span>
                  ))}
                </div>
              ))}
              <div className="statistics-plasma-heatmap-x-labels"><span />{Array.from({ length: 12 }, (_, index) => <span key={index + 1}>{(index + 1) % 2 === 0 ? index + 1 : ""}</span>)}</div>
              <div className="statistics-plasma-heatmap-x-title">Месяц</div>
            </div>
            <aside className="statistics-plasma-heatmap-legend" aria-label="Шкала числа ДТП">
              <strong>ДТП</strong>
              <div><i /><span><b>900</b><b>800</b><b>700</b><b>600</b><b>500</b><b>400</b><b>300</b></span></div>
            </aside>
          </div>
        </ReportCard>
        <ReportCard title="Сравнение месячной динамики 2023 и 2024 годов" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={recentYearsRows} margin={{ top: 18, right: 18, bottom: 8, left: 4 }} barGap={4}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 1000]} ticks={[0, 200, 400, 600, 800, 1000]} tick={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend verticalAlign="top" height={30} />
              <Bar dataKey="2023" name="2023" fill="#636efa" />
              <Bar dataKey="2024" name="2024" fill="#ef553b" />
            </BarChart>
          </ResponsiveContainer>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Структурные изменения</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Доля смертельных ДТП и ДТП с детьми" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={ANNUAL_ROWS} margin={{ top: 18, right: 18, bottom: 8, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis unit="%" tick={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend verticalAlign="top" height={30} />
              <Line type="linear" dataKey="fatalShare" name="Смертельные ДТП, %" stroke="#b91c1c" strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="linear" dataKey="childShare" name="ДТП с детьми, %" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Что изменилось к 2024 году" className="statistics-report-findings-card">
          <p>
            Количество ДТП выросло на <strong>12,2%</strong> к 2023 году. Число погибших увеличилось на <strong>12,3%</strong>,
            число пострадавших — на <strong>12,2%</strong>. Особенно заметно число ДТП с детьми: <strong>+28,8%</strong>.
          </p>
          <aside>
            Одновременно доля смертельных ДТП в 2024 году оставалась близкой к уровню 2023 года, поэтому рост абсолютного числа
            тяжёлых событий в значительной степени связан с общим увеличением аварийности.
          </aside>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Годовая таблица</h3></div>
      <div className="statistics-report-table-wrap">
        <table className="statistics-report-table">
          <thead><tr><th>Год</th><th>ДТП</th><th>Погибшие</th><th>Пострадавшие</th><th>Смертельные ДТП, %</th><th>ДТП с детьми, %</th></tr></thead>
          <tbody>{ANNUAL_ROWS.map((row) => <tr key={row.year}><th>{row.year}</th><td>{formatNumber.format(row.accidents)}</td><td>{formatNumber.format(row.deaths)}</td><td>{formatNumber.format(row.injured)}</td><td>{Number(row.fatalShare.toFixed(4))}</td><td>{Number(row.childShare.toFixed(4))}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
